/**
 * Custom hook for managing the Reading Panel feature.
 * Handles document upload, reading sessions, polling, and focus group launch.
 */
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "../i18n/LanguageContext";
import { useAuth } from "../utils/auth.jsx";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

export function useReading() {
  const { t, language } = useTranslation();

  // Stage: 'upload' | 'reading' | 'review' | 'launch' | 'running' | 'report'
  const [stage, setStage] = useState("upload");

  // Documents
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Reading session
  const [readingSessionId, setReadingSessionId] = useState(null);
  const [readingStatus, setReadingStatus] = useState(null);
  const [readingResults, setReadingResults] = useState(null);
  const [selectedPanelId, setSelectedPanelId] = useState("");
  const [readingError, setReadingError] = useState(null);

  // Focus group session (after launch)
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [roundSummaries, setRoundSummaries] = useState([]);
  const [moderatorSummary, setModeratorSummary] = useState("");
  const [finalAnalysis, setFinalAnalysis] = useState(null);
  const [beliefStates, setBeliefStates] = useState({});
  const [currentRound, setCurrentRound] = useState(0);
  const [currentSpeaker, setCurrentSpeaker] = useState(null);
  const [streamingContent, setStreamingContent] = useState("");
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const streamingContentRef = useRef("");
  const currentSpeakerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // ── Document Management ──

  const { token } = useAuth();

  const fetchDocuments = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/reading/documents`);
      if (res.ok) {
        const data = await res.json();
        setDocuments(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Failed to fetch documents:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const uploadDocument = useCallback(
    async (file, text, title) => {
      setUploading(true);
      setUploadError(null);
      try {
        let res;
        if (file) {
          const formData = new FormData();
          formData.append("file", file);
          if (title) formData.append("title", title);
          res = await fetch(`${API_BASE}/reading/documents/upload`, {
            method: "POST",
            body: formData,
          });
        } else if (text) {
          const formData = new FormData();
          formData.append("text", text);
          if (title) formData.append("title", title);
          res = await fetch(`${API_BASE}/reading/documents/upload`, {
            method: "POST",
            body: formData,
          });
        } else {
          throw new Error("No file or text provided");
        }

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Upload failed");
        }

        const data = await res.json();
        await fetchDocuments();
        setSelectedDocument(data);
        return data;
      } catch (err) {
        setUploadError(err.message);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [fetchDocuments],
  );

  const deleteDocument = useCallback(
    async (docId) => {
      try {
        const res = await fetch(`${API_BASE}/reading/documents/${docId}`, {
          method: "DELETE",
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Delete failed");
        }
        await fetchDocuments();
        if (selectedDocument?.document_id === docId) {
          setSelectedDocument(null);
        }
      } catch (err) {
        setUploadError(err.message);
      }
    },
    [fetchDocuments, selectedDocument],
  );

  const selectDocument = useCallback(async (docId) => {
    try {
      const res = await fetch(`${API_BASE}/reading/documents/${docId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedDocument(data);
      }
    } catch (err) {
      console.error("Failed to fetch document:", err);
    }
  }, []);

  // ── Reading Session ──

  const startReading = useCallback(async (docId, panelId) => {
    setReadingError(null);
    try {
      const res = await fetch(`${API_BASE}/reading/documents/${docId}/read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ panel_id: panelId }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Failed to start reading");
      }

      const data = await res.json();
      setReadingSessionId(data.reading_session_id);
      setStage("reading");
      startPolling(data.reading_session_id);
      return data;
    } catch (err) {
      setReadingError(err.message);
      throw err;
    }
  }, []);

  const startPolling = useCallback((rsId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/reading/sessions/${rsId}/status`);
        if (res.ok) {
          const status = await res.json();
          setReadingStatus(status);

          if (status.status === "complete" || status.status === "failed") {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;

            if (status.status === "complete") {
              // Fetch full results
              const fullRes = await fetch(
                `${API_BASE}/reading/sessions/${rsId}`,
              );
              if (fullRes.ok) {
                const results = await fullRes.json();
                setReadingResults(results);
                setStage("review");
              }
            } else {
              setReadingError("Reading session failed");
            }
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    };

    poll(); // Immediate first check
    pollIntervalRef.current = setInterval(poll, 3000);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const rereadPersona = useCallback(
    async (personaId) => {
      if (!readingSessionId) return;
      try {
        await fetch(
          `${API_BASE}/reading/sessions/${readingSessionId}/personas/${personaId}/reread`,
          {
            method: "POST",
          },
        );
        // Re-poll for updates
        startPolling(readingSessionId);
        setStage("reading");
      } catch (err) {
        console.error("Failed to trigger re-read:", err);
      }
    },
    [readingSessionId, startPolling],
  );

  // ── Launch Focus Group ──

  const handleStreamEvent = useCallback(
    (event) => {
      const type = event.type;

      if (type === "session_start") {
        setSessionId(event.session_id);
      } else if (type === "round_start") {
        setCurrentRound(event.round);
      } else if (type === "speaker_start") {
        const speaker = {
          name: event.speaker,
          id: event.speaker_id,
          round: event.round,
          model: event.model,
        };
        currentSpeakerRef.current = speaker;
        streamingContentRef.current = "";
        setCurrentSpeaker(speaker);
        setStreamingContent("");
      } else if (type === "content_chunk") {
        streamingContentRef.current += event.chunk;
        setStreamingContent(streamingContentRef.current);
      } else if (type === "speaker_end") {
        const finalContent = streamingContentRef.current;
        const speakerInfo = currentSpeakerRef.current;

        if (finalContent && finalContent.trim()) {
          const newMessage = {
            id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            speaker: speakerInfo?.name || event.speaker,
            speakerId: speakerInfo?.id || event.speaker_id,
            round: speakerInfo?.round || currentRound,
            content: finalContent.trim(),
            model: speakerInfo?.model,
          };
          setMessages((prev) => [...prev, newMessage]);
        }
        setCurrentSpeaker(null);
        setStreamingContent("");
      } else if (type === "round_summary") {
        setRoundSummaries((prev) => [...prev, event]);
      } else if (type === "belief_update") {
        setBeliefStates((prev) => ({
          ...prev,
          [event.persona_id]: [...(prev[event.persona_id] || []), event],
        }));
      } else if (type === "moderator_summary") {
        setModeratorSummary(event.summary);
      } else if (type === "final_analysis") {
        setFinalAnalysis(event.analysis);
      } else if (type === "session_complete") {
        setStage("guidance");
      }
    },
    [currentRound],
  );

  const launchFocusGroup = useCallback(
    async (launchConfig) => {
      if (!readingSessionId) return;

      setStage("running");
      setMessages([]);
      setRoundSummaries([]);
      setModeratorSummary("");
      setFinalAnalysis(null);
      setBeliefStates({});
      setCurrentRound(0);

      try {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        const res = await fetch(
          `${API_BASE}/reading/sessions/${readingSessionId}/launch-session`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(launchConfig),
            signal: controller.signal,
          },
        );

        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Failed to launch session");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const event = JSON.parse(line.slice(6));
                handleStreamEvent(event);
              } catch (e) {
                // Skip unparseable lines
              }
            }
          }
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Focus group stream error:", err);
          setReadingError(err.message);
        }
      }
    },
    [readingSessionId, handleStreamEvent],
  );

  const generateReport = useCallback(
    async (guidance = "") => {
      if (!sessionId) return;
      setIsGeneratingReport(true);
      setStage("report");

      try {
        const res = await fetch(`${API_BASE}/session/${sessionId}/report`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ guidance }),
        });

        if (res.ok) {
          const data = await res.json();
          setFinalAnalysis(data.analysis || data);
          setModeratorSummary(data.moderator_summary || moderatorSummary);
        }
      } catch (err) {
        console.error("Report generation failed:", err);
      } finally {
        setIsGeneratingReport(false);
      }
    },
    [sessionId, moderatorSummary],
  );

  // ── Reset ──

  const resetReading = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setStage("upload");
    setSelectedDocument(null);
    setReadingSessionId(null);
    setReadingStatus(null);
    setReadingResults(null);
    setSelectedPanelId("");
    setReadingError(null);
    setSessionId(null);
    setMessages([]);
    setRoundSummaries([]);
    setModeratorSummary("");
    setFinalAnalysis(null);
    setBeliefStates({});
    setCurrentRound(0);
    setCurrentSpeaker(null);
    setStreamingContent("");
    setIsGeneratingReport(false);
  }, []);

  return {
    // Stage
    stage,
    setStage,
    // Documents
    documents,
    selectedDocument,
    setSelectedDocument,
    selectDocument,
    uploading,
    uploadError,
    uploadDocument,
    deleteDocument,
    fetchDocuments,
    // Reading
    selectedPanelId,
    setSelectedPanelId,
    readingSessionId,
    readingStatus,
    readingResults,
    readingError,
    startReading,
    rereadPersona,
    // Focus group
    sessionId,
    messages,
    roundSummaries,
    moderatorSummary,
    finalAnalysis,
    beliefStates,
    currentRound,
    currentSpeaker,
    streamingContent,
    isGeneratingReport,
    launchFocusGroup,
    generateReport,
    // Control
    resetReading,
  };
}
